import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  HostListener,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NotificationService } from '../../../services/notification.service';
import { MatSelectModule } from '@angular/material/select';
import { Order, OrderStatus } from '../../../orders/models/order.models';
import { OrderUtils } from '../../../orders/utils/order.utils';
import { CANCEL_ORDER_ERROR_DETAIL } from '../../../orders/utils/orders-page-shared.utils';
import {
  VehicleCardComponent,
  Vehicle,
} from '../../../components/vehicle-card/vehicle-card.component';
import { PaymentInfoCardComponent } from '../../../components/payment-info-card/payment-info-card.component';
import {
  PaymentTransactionCardComponent,
  PaymentLineItem,
} from '../../../components/payment-transaction-card/payment-transaction-card.component';
import {
  PaymentTransactionHistory,
  CardDetails,
  BillingDetails,
  CreatePaymentLinkRequest,
  parseTransactionHistoryResponse,
  resolvePaymentTransactionDiscount,
  resolvePaymentTransactionPaidAmount,
} from '../../../services/payment.service';
import { PdfService, InvoiceData } from '../../../services/pdf.service';
import { FamilyPlanVehicle } from '../../../shared/types';
import { CancelOrderModalComponent } from '../../../components/cancel-order-modal/cancel-order-modal.component';
import { OrderCancellationSuccessModalComponent } from '../../../components/order-cancellation-success-modal/order-cancellation-success-modal.component';
import { PaymentLinkSentModalComponent } from '../../../components/payment-link-sent-modal/payment-link-sent-modal.component';
import {
  CustomInputComponent,
  ValidationRule,
} from '../../../components/custom-input/custom-input.component';
import { PhoneValidator } from '../../../shared/validators/phone.validator';
import { MobileDetectionMixin } from '../../../shared/mixins/mobile-detection.mixin';
import { loadStripe } from '@stripe/stripe-js';
import { environment } from '../../../../environments/environment';
import { getSuperAdminAuthToken } from '../../../shared/utils/auth-headers.util';
import { takeUntil } from 'rxjs/operators';
import {
  SuccessModalComponent,
  SuccessModalData,
} from '../../../shared/components/success-modal/success-modal.component';
import {
  buildPaymentSuccessModalData,
  extractPaymentSuccessFieldsFromApiOrder,
  normalizePackageNameForDisplay,
  PAYMENT_SUCCESS_MODAL_DEFAULTS,
  PaymentSuccessOrderFields,
} from '../../../shared/utils/payment-success-modal.util';
import { Subject } from 'rxjs';
import { ApiService } from '../../../services/api.service';
import { US_STATES } from '../../../shared/constants/us-states.constant';
import {
  createStripeElementConfig,
  STRIPE_ELEMENTS_APPEARANCE,
} from '../../../shared/utils/stripe-elements.util';
import { LoggerService } from '../../../services/logger.service';
import { runInvoiceDownloadAndNotify } from '../../../shared/utils/invoice-download-toast.util';
import { unwrapOrderFromApiResponse } from '../../../shared/utils/order-api.util';
import { cardBrandIcon, cardBrandName } from '../../../shared/utils/card-brand-icon.util';
import { PaymentInfo, resolvePaymentInfo } from '../../../shared/utils/payment-info.util';
import {
  buildTapToPayNativeParams,
  cleanTapToPayAmount,
} from '../../../shared/utils/tap-to-pay-order-params.util';
import { TapToPayAndroidNativeService } from '../../../services/tap-to-pay-android-native.service';
import {
  BuiltInPaymentMethodCode,
  PaymentMethodCode,
} from '../../../shared/types/payment-method.enum';
import {
  formatDateInTimeZone,
  formatDateTimeInTimeZone,
  getUserTimezone,
} from '../../../shared/utils/date.util';
import { UsaPhoneDisplayPipe } from '../../../shared/validators/usa-phone-display.pipe';
import { SelectSearchComponent } from '../../../shared/components/select-search/select-search.component';
import { SelectFilterPipe } from '../../../shared/pipes/select-filter.pipe';

interface PaymentForm {
  method: BuiltInPaymentMethodCode;
  phoneNumber: string;
  cardholderName: string;
  billingAddress: string;
}

@Component({
  selector: 'app-super-admin-order-details-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatSelectModule,
    VehicleCardComponent,
    PaymentInfoCardComponent,
    PaymentTransactionCardComponent,
    CancelOrderModalComponent,
    OrderCancellationSuccessModalComponent,
    PaymentLinkSentModalComponent,
    CustomInputComponent,
    SuccessModalComponent,
    UsaPhoneDisplayPipe,
    SelectSearchComponent,
    SelectFilterPipe,
  ],
  templateUrl: './super-admin-order-details-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./super-admin-order-details-modal.component.scss'],
})
export class SuperAdminOrderDetailsModalComponent
  extends MobileDetectionMixin
  implements AfterViewInit, OnChanges, OnDestroy
{
  private readonly logger = inject(LoggerService);
  private readonly tapToPayAndroidNative = inject(TapToPayAndroidNativeService);
  /** Cache for {@link resolveDisplayTimezone}; cleared when the `order` input changes. */
  private displayTimezone: string | null = null;
  @Input() order: Order | null = null;
  @Input() isOpen = false;
  @Input() companyId = '';
  @Output() closeModal = new EventEmitter<void>();
  @Output() orderCancelled = new EventEmitter<Order>();
  @Output() bookAgain = new EventEmitter<void>();
  @Output() orderUpdated = new EventEmitter<void>();

  @ViewChild('orderTabListContainer') orderTabListContainer?: ElementRef<HTMLDivElement>;

  private destroy$ = new Subject<void>();

  activeTab = 'order-summary';
  showDropdown = false;

  // Cancel order modal
  showCancelOrderModal = false;

  // Cancellation success modal
  showCancellationSuccessModal = false;
  cancellationRefundAmount = '';
  cancellationWasScheduled = false;

  // Family plan state
  familyPlanVehicles: FamilyPlanVehicle[] = [];
  selectedVehicleId: string | null = null;
  editingFamilyVehicle: FamilyPlanVehicle | null = null;

  // Edit mode state for Vehicle Details
  isVehicleEditMode = false;
  vehicleFetchSuccess = false;
  hasAttemptedFetch = false; // Track if fetch has been attempted
  isLoadingVehicleDetails = false; // Track loading state for vehicle details from API

  // Mobile menu state for vehicle actions
  showVehicleMobileMenu = false;
  vehicleForm: {
    state: string;
    licensePlate: string;
    vehicle: string;
    year: string;
    make: string;
    model: string;
    fetchedVehicleId?: string; // Store the fetched vehicle ID
  } = {
    state: '',
    licensePlate: '',
    vehicle: '',
    year: '',
    make: '',
    model: '',
  };

  // Shared state list used across vehicle forms.
  readonly states = US_STATES;

  // Vehicle fetch properties
  isLoadingVehicle = false;

  // Getter for canFetchVehicle - enable when state and license plate are provided
  get canFetchVehicle(): boolean {
    return !!(this.vehicleForm.state && this.vehicleForm.licensePlate);
  }

  tabs = [
    { id: 'order-summary', label: 'Order Summary', icon: 'clipboard_list' },
    { id: 'customer-info', label: 'Booking Details', icon: 'user' },
    { id: 'vehicle-details', label: 'Vehicle Details', icon: 'car' },
    { id: 'payment', label: 'Payment Details', icon: 'credit_card' },
  ];

  // Tab scrolling state
  canScrollLeft = false;
  canScrollRight = true;

  transactionHistories: PaymentTransactionHistory[] = [];
  cardDetails: CardDetails | null = null;
  billingDetails: BillingDetails | null = null;
  isLoadingPaymentDetails = false;
  paymentDetailsError = false;

  // Payment form for pending payments
  paymentForm: PaymentForm = {
    method: this.getDefaultPaymentMethod(),
    phoneNumber: '',
    cardholderName: '',
    billingAddress: '',
  };

  /**
   * Get default payment method based on device type
   * Mobile/Tablet: tap-to-pay (supports NFC)
   * Desktop: card (no NFC support)
   */
  private getDefaultPaymentMethod(): BuiltInPaymentMethodCode {
    return this.isMobileOrTablet ? PaymentMethodCode.TapToPay : PaymentMethodCode.Card;
  }

  // Payment processing state
  isProcessingPayment = false;
  isCreatingPaymentLink = false;
  paymentLinkError = '';
  showPaymentLinkSentModal = false;
  paymentIntentClientSecret = '';

  // Payment success modal
  showPaymentSuccess = false;
  successModalData: SuccessModalData = { ...PAYMENT_SUCCESS_MODAL_DEFAULTS };

  // Stripe Elements
  stripe: any;
  elements: any;
  cardNumberElement: any;
  cardExpiryElement: any;
  cardCvcElement: any;
  // Track Stripe Elements completion state
  stripeCardNumberComplete = false;
  stripeCardExpiryComplete = false;
  stripeCardCvcComplete = false;

  // Phone validation for payment form
  paymentPhoneValidation: ValidationRule[] = [
    { type: 'required', message: 'Phone Number is required' },
    {
      type: 'custom',
      value: (value: string) => {
        if (!value) return true;
        return this.isValidUsOrIndianPhone(value);
      },
      message: 'Please enter a valid US phone number',
    },
  ];

  constructor(
    private pdfService: PdfService,
    private messageService: NotificationService,
    private apiService: ApiService
  ) {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // A new order carries its own location zone, so drop the memoized one (CWS-1510).
    if (changes['order']) {
      this.displayTimezone = null;
    }
    // Reset state when modal is closed
    if (
      changes['isOpen'] &&
      changes['isOpen'].previousValue === true &&
      changes['isOpen'].currentValue === false
    ) {
      this.resetModalState();
    }
  }

  ngAfterViewInit(): void {
    // Initialize arrow visibility once view is ready
    setTimeout(() => this.updateTabScrollState());
    // Preload payment transaction if we already have an order id
    setTimeout(() => this.fetchTransactionHistory(), 0);
  }

  /**
   * Clean up resources when the component is destroyed: emits to and completes the destroy$ subject and safely unmounts any Stripe card elements if they exist.
   * @example
   * gOnDestroy()
   * undefined
   * @returns {void} Nothing.
   **/
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    // Unmount Stripe elements if they exist
    if (this.cardNumberElement) {
      try {
        this.cardNumberElement.unmount();
      } catch (e) {
        // Already unmounted
      }
    }
    if (this.cardExpiryElement) {
      try {
        this.cardExpiryElement.unmount();
      } catch (e) {
        // Already unmounted
      }
    }
    if (this.cardCvcElement) {
      try {
        this.cardCvcElement.unmount();
      } catch (e) {
        // Already unmounted
      }
    }
  }

  onTabScroll(): void {
    this.updateTabScrollState();
  }

  private updateTabScrollState(): void {
    const container = this.orderTabListContainer?.nativeElement;
    if (!container) return;
    this.canScrollLeft = container.scrollLeft > 0;
    this.canScrollRight =
      Math.ceil(container.scrollLeft + container.clientWidth) < container.scrollWidth;
  }

  scrollTabs(direction: 'left' | 'right'): void {
    const container = this.orderTabListContainer?.nativeElement;
    if (!container) return;
    const delta = container.clientWidth * 0.8;
    // Scroll in the natural direction
    container.scrollBy({ left: direction === 'right' ? delta : -delta, behavior: 'smooth' });
    // Update arrow visibility after the scroll animation
    setTimeout(() => this.updateTabScrollState(), 300);
  }

  onClose(): void {
    this.resetModalState();
    this.closeModal.emit();
  }

  /**
   * Reset the modal's internal state, forms, loading flags, and UI scrolling positions to their initial defaults.
   * @example
   * resetModalState()
   * undefined
   * @returns {void} No return value.
   **/
  private resetModalState(): void {
    // Reset all modal state to initial values
    this.activeTab = 'order-summary';
    this.isVehicleEditMode = false;
    this.vehicleFetchSuccess = false;
    this.hasAttemptedFetch = false;
    this.isLoadingVehicleDetails = false;
    this.transactionHistories = [];
    this.cardDetails = null;
    this.billingDetails = null;
    this.isLoadingPaymentDetails = false;
    this.paymentDetailsError = false;
    this.showDropdown = false;
    this.familyPlanVehicles = [];
    this.selectedVehicleId = null;
    this.editingFamilyVehicle = null;

    // Reset payment form
    this.paymentForm = {
      method: this.getDefaultPaymentMethod(),
      phoneNumber: '',
      cardholderName: '',
      billingAddress: '',
    };
    this.isProcessingPayment = false;
    this.isCreatingPaymentLink = false;
    this.paymentLinkError = '';
    this.paymentIntentClientSecret = '';
    this.stripeCardNumberComplete = false;
    this.stripeCardExpiryComplete = false;
    this.stripeCardCvcComplete = false;

    // Reset vehicle form
    this.vehicleForm = {
      state: '',
      licensePlate: '',
      vehicle: '',
      year: '',
      make: '',
      model: '',
    };

    // Reset scroll state
    this.canScrollLeft = false;
    this.canScrollRight = false;

    // Reset tab scroll position
    setTimeout(() => {
      if (this.orderTabListContainer?.nativeElement) {
        this.orderTabListContainer.nativeElement.scrollLeft = 0;
      }
    }, 0);
  }

  /**
   * Set the active tab and perform tab-specific initialization (payment, vehicle-details, and visibility adjustments).
   * @example
   * etActiveTab('payment')
   * undefined
   * @param {{string}} {{tabId}} - The id of the tab to activate (e.g., 'payment' or 'vehicle-details').
   * @returns {{void}} No return value; performs side effects like initializing payment, loading vehicles, and ensuring visibility.
   **/
  setActiveTab(tabId: string): void {
    this.logger.debug('Ã°Å¸Å¡â€” setActiveTab called with:', tabId);
    this.activeTab = tabId;
    if (tabId === 'payment') {
      // Ensure payment method is valid for current device
      // If on desktop and method is tap-to-pay, switch to card
      if (!this.isMobileOrTablet && this.paymentForm.method === PaymentMethodCode.TapToPay) {
        this.paymentForm.method = PaymentMethodCode.Card;
      }
      // If on mobile and method is not set, default to tap-to-pay
      if (this.isMobileOrTablet && !this.paymentForm.method) {
        this.paymentForm.method = PaymentMethodCode.TapToPay;
      }

      this.fetchTransactionHistory();
      // Initialize phone number from order if available
      if (this.order?.phone && !this.paymentForm.phoneNumber) {
        this.paymentForm.phoneNumber = this.order.phone;
      }
      // Fetch payment intent if payment is pending
      if (this.isPaymentPending()) {
        this.fetchPaymentIntent();
      }
      // Initialize Stripe when payment tab is opened
      setTimeout(() => {
        if (this.paymentForm.method === PaymentMethodCode.Card) {
          if (this.stripe && this.cardNumberElement) {
            this.mountStripeElements();
          } else if (!this.stripe) {
            this.initializeStripeIfNeeded().then(() => {
              this.mountStripeElements();
            });
          }
        }
      }, 200);
    }
    if (tabId === 'vehicle-details') {
      this.logger.debug(
        'Ã°Å¸Å¡â€” Vehicle details tab activated, calling tryLoadFamilyPlanVehicles...'
      );
      this.tryLoadFamilyPlanVehicles();
    }
    // Ensure the active tab is fully visible
    setTimeout(() => this.ensureActiveTabFullyVisible(), 0);
  }

  /**
   * Ensure the currently active tab element in the order tab list is fully visible by smoothly scrolling the tab container if it is partially out of view.
   * @example
   * ensureActiveTabFullyVisible()
   * undefined
   * @returns {void} No return value.
   **/
  private ensureActiveTabFullyVisible(): void {
    const container = this.orderTabListContainer?.nativeElement;
    if (!container) return;
    const activeEl = container.querySelector('.tab-item.active') as HTMLElement | null;
    if (!activeEl) return;
    const padding = 60; // account for arrow overlays and some spacing
    const elLeft = activeEl.offsetLeft - padding;
    const elRight = activeEl.offsetLeft + activeEl.offsetWidth + padding;
    const viewLeft = container.scrollLeft;
    const viewRight = viewLeft + container.clientWidth;
    if (elLeft < viewLeft) {
      container.scrollTo({ left: elLeft, behavior: 'smooth' });
    } else if (elRight > viewRight) {
      container.scrollTo({ left: elRight - container.clientWidth, behavior: 'smooth' });
    }
    this.updateTabScrollState();
  }

  getStatusClass(status: OrderStatus): string {
    return `status-${status.color}`;
  }

  /**
   * Public method to refresh payment tab data
   * Can be called from parent component when payment status changes
   * Refreshes payment data regardless of which tab is currently active
   */
  refreshPaymentTab(): void {
    if (this.order?.id) {
      this.fetchTransactionHistory();
      // Also refresh payment intent if payment is pending
      if (this.isPaymentPending()) {
        this.fetchPaymentIntent();
      }
      // Also refresh the order data to get updated status
      this.apiService
        .getWithSuperAdminToken(`order/${this.order.id}`)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (orderResponse: any) => {
            const apiOrder = orderResponse?.data || orderResponse;
            if (apiOrder) {
              // Update order status if it changed
              const newStatus = this.mapOrderStatus(apiOrder.order_status, apiOrder.payment_status);
              if (this.order) {
                this.order.status = newStatus;
              }
            }
          },
          error: error => {
            this.logger.error('Error refreshing order data:', error);
          },
        });
    }
  }

  private mapOrderStatus(orderStatus: string, paymentStatus: string): OrderStatus {
    return OrderUtils.mapOrderStatus(orderStatus, paymentStatus);
  }

  /**
   * Fetches transaction history and payment details for the current order and company, updating component state (transactionHistories, cardDetails, billingDetails) and loading/error flags.
   * @example
   * this.fetchTransactionHistory()
   * undefined
   * @returns {void} Does not return a value; updates component properties with fetched data or clears them on error.
   **/
  private fetchTransactionHistory(): void {
    if (!this.order?.id || !this.companyId) return;

    this.isLoadingPaymentDetails = true;
    this.paymentDetailsError = false;
    this.transactionHistories = [];
    this.cardDetails = null;
    this.billingDetails = null;

    this.apiService
      .getWithSuperAdminToken(
        `admin/super-admin/companies/${this.companyId}/transactions/${this.order.id}`
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.isLoadingPaymentDetails = false;
          const th = res?.data?.transactionHistory || res?.transactionHistory;
          if (!th) {
            this.paymentDetailsError = true;
            return;
          }

          const parsed = parseTransactionHistoryResponse(th);
          this.transactionHistories = parsed.transactions;
          this.cardDetails = parsed.cardDetails;
          this.billingDetails = parsed.billingDetails;
        },
        error: (error: any) => {
          this.isLoadingPaymentDetails = false;
          this.paymentDetailsError = true;
          // Clear any previous data
          this.transactionHistories = [];
          this.cardDetails = null;
          this.billingDetails = null;
        },
      });
  }

  formatAmountCents(amountInMinorUnits: string | number | null | undefined): string {
    const numeric = Number(amountInMinorUnits ?? 0);
    const dollars = isFinite(numeric) ? numeric : 0;
    return `$${dollars.toFixed(2)}`;
  }

  formatAmount(amount: string): string {
    if (!amount) return '$0.00';
    const numeric = Number(amount);
    if (!isFinite(numeric)) return '$0.00';

    // Amount is already in dollars
    const dollars = numeric;
    return `$${dollars.toFixed(2)}`;
  }

  getTransactionItems(th: PaymentTransactionHistory): PaymentLineItem[] {
    const items: PaymentLineItem[] = [
      { label: th.package_name || 'Express Wash Membership', amount: this.formatAmount(th.amount) },
    ];
    // Derived from what this payment collected, so the lines reconcile with the total below
    // them; a later membership renewal can carry a different discount than the signup order.
    const discount = resolvePaymentTransactionDiscount(th, Number(th.amount || 0));
    if (th.voucher_code && discount > 0) {
      items.push({ label: `${th.voucher_code} Discount`, amount: `-$${discount.toFixed(2)}` });
    }
    items.push({ label: 'Taxes + Fees', amount: '$0.00' });
    return items;
  }

  /**
   * Calculate and format the total amount paid for a payment transaction as a USD string.
   * @example
   * etTotalAmountPaid({ amount: '100.00', discount: '10.00' })
   * $90.00
   * @param {{PaymentTransactionHistory}} {{th}} - Payment transaction history object containing amount (in dollars) and optional discount.
   * @returns {{string}} Formatted total amount paid as a USD string (e.g., "$90.00").
   **/
  getTotalAmountPaid(th: PaymentTransactionHistory): string {
    // What this payment actually collected, when the backend supplies it. The arithmetic below
    // works off order-level columns that are frozen at signup, so on a membership that renews at
    // different amounts it reports the same total for every renewal.
    const paid = resolvePaymentTransactionPaidAmount(th);
    if (paid !== null) {
      return `$${Math.max(0, paid).toFixed(2)}`;
    }

    if (!th.amount) return '$0.00';

    // Amount is already in dollars
    const numericAmount = Number(th.amount);
    const baseAmount = numericAmount;

    const discount = Number(th.discount || '0.00');
    const taxes = 0; // No taxes in the current API response
    const total = baseAmount - discount + taxes;
    return `$${Math.max(0, total).toFixed(2)}`;
  }

  /**
   * The zone the modal's date formatters render in: the order's location zone, else the
   * shop-owner zone from local state; '' (browser-local) when neither is known. Memoized until
   * the `order` input changes so template-bound formatters never re-read localStorage on a
   * change-detection pass. Restores CWS-890/891 behaviour lost in the CWS-847 merge (CWS-1510).
   * @returns {string}
   */
  private resolveDisplayTimezone(): string {
    if (this.displayTimezone === null) {
      this.displayTimezone = this.order?.locationTimezone?.trim() || getUserTimezone();
    }
    return this.displayTimezone;
  }

  formatPaymentDate(dateString: string): string {
    if (!dateString) return '';
    return formatDateInTimeZone(dateString, this.resolveDisplayTimezone(), {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  /**
   * Convert a date-time string into a readable format in the location timezone,
   * returning an empty string for falsy or invalid input.
   * @example
   * formatPaymentDateTime('2023-08-15T14:30:00Z')
   * "Aug 15, 2023, 2:30 PM" (when location TZ is America/Los_Angeles and that is the local wall time)
   * @param {{string}} {{dateString}} - The date-time string to format (parseable by Date).
   * @returns {{string}} Formatted date-time in the shop/location timezone, or an empty string if the input is falsy or invalid.
   **/
  formatPaymentDateTime(dateString: string): string {
    if (!dateString) return '';
    return formatDateTimeInTimeZone(dateString, this.resolveDisplayTimezone());
  }

  /** The Payment Info box: the order's latest charge and the card it was made with. */
  private get paymentInfo(): PaymentInfo {
    return resolvePaymentInfo(this.transactionHistories, this.cardDetails, this.billingDetails);
  }

  getCardMask(): string {
    const card = this.paymentInfo.card;
    return card?.last4 ? `XXX-${card.last4}` : '';
  }

  hasCardDetails(): boolean {
    const last4 = this.paymentInfo.card?.last4;
    return typeof last4 === 'string' && last4.length > 0;
  }

  getCardExpirationDate(): string {
    if (!this.cardDetails?.exp_month || !this.cardDetails?.exp_year) return '';
    const month = this.cardDetails.exp_month.toString().padStart(2, '0');
    const year = this.cardDetails.exp_year;
    return `${month}/${year}`;
  }

  getCardBrandIcon(): string {
    return cardBrandIcon(this.paymentInfo.card?.brand);
  }

  getCardBrandName(): string {
    return cardBrandName(this.paymentInfo.card?.brand);
  }

  getTransactionId(): string {
    const paymentIntentId = this.paymentInfo.transaction?.payment_intent_id;
    return paymentIntentId ? `#${paymentIntentId.slice(-10)}` : '';
  }

  /**
   * Returns a formatted billing address composed of line1 and country, or an empty string if not available.
   * @example
   * etBillingAddress()
   * '123 Main St, USA'
   * @param {{void}} {{none}} - No arguments.
   * @returns {{string}} Return the billing address as a single comma-separated string or an empty string.
   **/
  getBillingAddress(): string {
    const billing = this.paymentInfo.billing;
    if (!billing?.address) {
      return '';
    }
    const parts = [];
    if (billing.address.line1) {
      parts.push(billing.address.line1);
    }
    if (billing.address.country) {
      parts.push(billing.address.country);
    }
    return parts.length > 0 ? parts.join(', ') : '';
  }

  /**
   * Build a single contact string from available billing or order contact details.
   * @example
   * etContactInfo()
   * "jane.doe@example.com, +1-555-123-4567"
   * @returns {string} Comma-separated contact information (email and/or phone) or an empty string if none found.
   **/
  getContactInfo(): string {
    const billing = this.paymentInfo.billing;
    const parts = [];
    if (billing?.email) {
      parts.push(billing.email);
    }
    if (billing?.phone) {
      parts.push(billing.phone);
    }
    if (parts.length === 0) {
      // Fallback to order data
      if (this.order?.email) parts.push(this.order.email);
      if (this.order?.phone) parts.push(this.order.phone);
    }
    return parts.length > 0 ? parts.join(', ') : '';
  }

  getPaymentDate(): string {
    const paymentDate = this.paymentInfo.transaction?.payment_date;
    return paymentDate ? this.formatPaymentDateTime(paymentDate) : '';
  }

  /**
   * Download or generate an invoice for a given transaction by fetching business info with a super admin token and showing a notification; falls back to default values on failure.
   * @example
   * nDownloadInvoice({ id: 'txn_123', amount: 100, ... } as PaymentTransactionHistory)
   * void
   * @param {{PaymentTransactionHistory}} {{transaction}} - The payment transaction for which to generate/download the invoice.
   * @returns {{void}} No return value; triggers API call and displays or downloads the invoice via side effects.
   **/
  onDownloadInvoice(transaction: PaymentTransactionHistory): void {
    if (!this.order) return;

    // Fetch business info from API using super admin token
    this.apiService
      .getWithSuperAdminToken('company/me')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            const businessData = response.data;
            const invoiceData: InvoiceData = {
              order: this.order!,
              transaction: transaction,
              customerInfo: {
                name: this.order!.customerName,
                email: this.order!.email,
                phone: this.order!.phone,
                address: this.order!.branch || 'N/A', // Use location from order
              },
              businessInfo: {
                name: businessData.name || 'Way.com Car Wash',
                address: '', // Address removed from header
                phone: businessData.contact || '(555) 123-4567',
                email: businessData.email || 'support@way.com',
              },
              cardDetails: this.cardDetails,
              billingDetails: this.billingDetails,
            };

            this.generateAndShowInvoiceNotification(invoiceData);
          } else {
            // Fallback to default values if API fails
            this.generateInvoiceWithDefaults(transaction);
          }
        },
        error: error => {
          this.logger.error('Error fetching business profile:', error);
          // Fallback to default values if API fails
          this.generateInvoiceWithDefaults(transaction);
        },
      });
  }

  /**
   * Generate an invoice object with default business and customer values and trigger an invoice notification.
   * @example
   * generateInvoiceWithDefaults({ id: 'txn_123', amount: 49.99 })
   * undefined
   * @param {PaymentTransactionHistory} transaction - Payment transaction history used to populate the invoice.
   * @returns {void} Does not return a value.
   **/
  private generateInvoiceWithDefaults(transaction: PaymentTransactionHistory): void {
    if (!this.order) return;

    const invoiceData: InvoiceData = {
      order: this.order,
      transaction: transaction,
      customerInfo: {
        name: this.order.customerName || 'N/A',
        email: this.order.email || 'N/A',
        phone: this.order.phone || 'N/A',
        address: this.order.branch || 'N/A', // Use location from order
      },
      businessInfo: {
        name: 'Way.com Car Wash',
        address: '',
        phone: '(555) 123-4567',
        email: 'support@way.com',
      },
      cardDetails: this.cardDetails,
      billingDetails: this.billingDetails,
    };

    this.generateAndShowInvoiceNotification(invoiceData);
  }

  private generateAndShowInvoiceNotification(invoiceData: InvoiceData): void {
    runInvoiceDownloadAndNotify(this.messageService, () => {
      this.pdfService.generateInvoice(invoiceData);
    });
  }

  /**
   * Check if payment is pending for the order
   */
  isPaymentPending(): boolean {
    if (!this.order) return false;

    // Payment is pending if:
    // 1. Order status is pending, AND
    // 2. No transaction history exists (or transaction history is empty)
    const isPendingStatus = this.order.status.value.toLowerCase() === 'pending';
    const hasNoTransactions = this.transactionHistories.length === 0;

    return isPendingStatus && hasNoTransactions && !this.isLoadingPaymentDetails;
  }

  /**
   * Select payment method
   */
  /**
   * Open Tap to Pay app using deep link with order details
   *
   * Deep Link Format:
   * taptopay://payment?amount=20&orderId=82498271192&currency=USD&phoneNumber=2122333341&email=user@example.com&source=web&customerId=123&locationId=456&id=789&admin_user_id=123&wash_type=single_wash&package_id=456&vehicle_id=789
   *
   * Parameters passed:
   * - amount: The total payment amount (just number, e.g., "20")
   * - orderId: The order ID without # symbol (e.g., "82498271192")
   * - currency: Currency code (default: "USD")
   * - phoneNumber: Customer phone number (from paymentForm or order)
   * - email: Customer email (from order, optional)
   * - source: Order source (from order, default: "web")
   * - customerId: Customer ID from API
   * - locationId: Location ID from API
   * - id: Internal order ID from API
   * - admin_user_id: User ID from API (named as admin_user_id in deep link)
   * - wash_type: Wash type from API (e.g., "single_wash", "monthly_membership")
   * - package_id: Package ID from API
   * - vehicle_id: Vehicle ID from API
   */
  openTapToPayApp(): void {
    if (!this.order) {
      this.logger.error('No order available for Tap to Pay');
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Order information is not available.',
        life: 3000,
      });
      return;
    }

    // Store order in local variable to avoid TypeScript null checks
    const order = this.order;

    // Fetch full order details from API to get customer_id and location_id
    if (!order.id) {
      this.logger.error('No order ID available for Tap to Pay');
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Order ID is not available.',
        life: 3000,
      });
      return;
    }

    // Fetch order details to get customer_id and location_id
    this.apiService
      .getWithSuperAdminToken(`order/${order.id}`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (orderResponse: any) => {
          const apiOrder = (unwrapOrderFromApiResponse(orderResponse) ?? {}) as Record<string, any>;
          const params = this.buildSuperAdminTapToPayParams(order, apiOrder);
          this.sendTapToPayToNativeApp(params);
        },
        error: error => {
          this.logger.error('Error fetching order details for Tap to Pay:', error);
          // Fallback: use available order data without customer_id and location_id
          this.openTapToPayAppWithFallback();
        },
      });
  }

  /**
   * Fallback method to open Tap to Pay with available order data (without API fetch)
   */
  private buildSuperAdminTapToPayParams(
    order: Order,
    apiOrder: Record<string, any>
  ): URLSearchParams {
    const authToken = getSuperAdminAuthToken();

    return buildTapToPayNativeParams({
      apiOrder,
      fallbackOrderId: order.id || order.orderId,
      source: 'saas',
      amount: cleanTapToPayAmount(order.price || apiOrder['final_price']),
      customerId: String(
        apiOrder['customer_id'] ?? apiOrder['customerId'] ?? order.customerId ?? ''
      ),
      locationId: String(apiOrder['location_id'] ?? apiOrder['locationId'] ?? ''),
      adminUserId: String(apiOrder['user_id'] ?? ''),
      washType: String(apiOrder['wash_type'] ?? order.washTypeValue ?? ''),
      packageId: String(apiOrder['package_id'] ?? ''),
      vehicleId: String(apiOrder['vehicle_id'] ?? order.vehicleId ?? ''),
      stripeAccountId: String(apiOrder['stripe_account_id'] ?? ''),
      phoneNumber:
        this.paymentForm.phoneNumber || order.phone || String(apiOrder['customer_contact'] ?? ''),
      email: order.email || String(apiOrder['customer_email'] ?? ''),
      authToken,
      publicOrderId: String(order.orderId || apiOrder['order_id'] || apiOrder['orderId'] || ''),
    });
  }

  private openTapToPayAppWithFallback(): void {
    if (!this.order) return;
    const params = this.buildSuperAdminTapToPayParams(this.order, {});
    this.sendTapToPayToNativeApp(params);
  }

  private sendTapToPayToNativeApp(params: URLSearchParams): void {
    if (!this.tapToPayAndroidNative.isAvailable) {
      console.warn('[TapToPay] Native Android shell required (TapToPayAndroid bridge missing).');
      return;
    }
    this.tapToPayAndroidNative.startPaymentFromParams(params);
  }

  /**
   * Selects and configures the payment method for the form, performing method-specific initialization (e.g., prefill phone for link, fetch payment intent and mount Stripe Elements for card).
   * @example
   * selectPaymentMethod(PaymentMethodCode.Card)
   * @param method The payment method to select: 'tap-to-pay', 'card', or 'link'.
   */
  selectPaymentMethod(method: BuiltInPaymentMethodCode): void {
    this.paymentForm.method = method;
    // Initialize phone number from order if available
    if (method === PaymentMethodCode.Link && !this.paymentForm.phoneNumber && this.order?.phone) {
      this.paymentForm.phoneNumber = this.order.phone;
    }
    if (method === PaymentMethodCode.Card) {
      // Fetch payment intent if not already fetched
      if (!this.paymentIntentClientSecret && this.isPaymentPending()) {
        this.fetchPaymentIntent();
      }
      // Initialize and mount Stripe Elements when card payment is selected
      setTimeout(() => {
        if (this.stripe && this.cardNumberElement) {
          this.mountStripeElements();
        } else if (!this.stripe) {
          this.initializeStripeIfNeeded().then(() => {
            this.mountStripeElements();
          });
        }
      }, 200);
    }
  }

  /**
   * Validate payment form
   */
  isPaymentFormValid(): boolean {
    if (this.paymentForm.method === PaymentMethodCode.TapToPay) {
      return true;
    }
    if (this.paymentForm.method === PaymentMethodCode.Card) {
      const hasCardholder = Boolean(this.paymentForm.cardholderName?.trim());
      const usingStripeElements =
        this.cardNumberElement && this.cardExpiryElement && this.cardCvcElement;
      if (usingStripeElements) {
        return (
          hasCardholder &&
          this.stripeCardNumberComplete &&
          this.stripeCardExpiryComplete &&
          this.stripeCardCvcComplete
        );
      }
      return hasCardholder;
    }
    return this.isValidUsOrIndianPhone((this.paymentForm.phoneNumber || '').trim());
  }

  /**
   * Validate US phone number
   * Supports all US number formats:
   * - +16813813819 (with country code +1)
   * - 6813813819 (10 digits)
   * - (681) 381-3819
   * - 681-381-3819
   * - 681.381.3819
   * - 681 381 3819
   */
  isValidUsOrIndianPhone(phoneNumber: string): boolean {
    if (!phoneNumber) return false;

    const trimmed = phoneNumber.trim();
    if (!trimmed) return false;

    // Extract digits only
    const digitsOnly = trimmed.replace(/\D/g, '');

    if (trimmed.startsWith('+1') && digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
      const usDigits = digitsOnly.substring(1);
      if (usDigits.length === 10) {
        const areaCode = usDigits.substring(0, 3);
        if (areaCode.charAt(0) !== '0' && areaCode.charAt(0) !== '1') {
          return true;
        }
      }
    }
    if (digitsOnly.length === 10) {
      const areaCode = digitsOnly.substring(0, 3);
      if (areaCode.charAt(0) !== '0' && areaCode.charAt(0) !== '1') {
        return true;
      }
    }

    // Also check using PhoneValidator for standard US formats (without country code)
    const control = { value: trimmed } as any;
    if (PhoneValidator.usaPhone(control) === null) {
      return true;
    }

    return false;
  }

  /**
   * Create payment link for pending order
   * Same implementation as add order modal - passes order_id, phone_number, email, and source
   */
  createPaymentLink(): void {
    if (!this.order?.id || !this.paymentForm.phoneNumber) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Order ID or phone number is missing',
      });
      return;
    }

    this.isCreatingPaymentLink = true;
    this.paymentLinkError = '';

    const request: CreatePaymentLinkRequest = {
      order_id: this.order.id,
      phone_number: this.paymentForm.phoneNumber,
      email: this.order.email || undefined,
      source: this.order.source || 'web',
    };
    const { order_id, phone_number, email, source } = request;
    this.logger.debug('Ã°Å¸â€â€” Creating payment link with request:', request);

    let endpoint = 'payment/create-payment-link';
    if (source) {
      endpoint += `?source=${source}`;
    }
    const requestBody: any = { order_id, phone_number };
    if (email) {
      requestBody.email = email;
    }

    this.apiService
      .postWithSuperAdminToken(endpoint, requestBody)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.isCreatingPaymentLink = false;
          const paymentLinkResponse = response?.data || response;
          this.logger.debug('Ã¢Å“â€¦ Payment link created successfully:', paymentLinkResponse);

          if (paymentLinkResponse.status && paymentLinkResponse.paymentLink?.success) {
            this.messageService.add({
              severity: 'success',
              summary: 'Payment Link Created',
              detail: `Payment link sent to ${this.paymentForm.phoneNumber}`,
            });

            // Show payment link sent modal
            this.showPaymentLinkSentModal = true;

            // Refresh transaction history after a short delay
            setTimeout(() => {
              this.fetchTransactionHistory();
              this.orderUpdated.emit();
            }, 1000);
          } else {
            this.paymentLinkError = paymentLinkResponse.message || 'Failed to create payment link';
            this.messageService.add({
              severity: 'error',
              summary: 'Payment Link Error',
              detail: this.paymentLinkError,
            });
          }
        },
        error: error => {
          this.isCreatingPaymentLink = false;
          this.logger.error('Ã¢ÂÅ’ Error creating payment link:', error);

          let errorMessage = 'Failed to create payment link. Please try again.';
          if (error.status === 500) {
            errorMessage = 'Server error occurred. Please try again later.';
          } else if (error.status === 401) {
            errorMessage = 'Authentication failed. Please login again.';
          } else if (error.status === 400) {
            errorMessage =
              error.error?.message || 'Invalid request data. Please check your information.';
          } else if (error.error?.message) {
            errorMessage = error.error.message;
          }

          this.paymentLinkError = errorMessage;
          this.messageService.add({
            severity: 'error',
            summary: 'Payment Link Error',
            detail: this.paymentLinkError,
          });
        },
      });
  }

  /**
   * Process card payment for pending order
   */
  async processCardPayment(): Promise<void> {
    this.logger.debug('Ã°Å¸â€™Â³ processCardPayment called');
    this.logger.debug('Ã°Å¸â€™Â³ Button clicked - starting payment process');

    if (!this.isPaymentFormValid()) {
      this.logger.debug('Ã¢ÂÅ’ Payment form validation failed');
      this.logger.debug('Validation details:', {
        hasCardholder: Boolean(this.paymentForm.cardholderName?.trim()),
        hasAddress: Boolean(this.paymentForm.billingAddress?.trim()),
        stripeCardNumberComplete: this.stripeCardNumberComplete,
        stripeCardExpiryComplete: this.stripeCardExpiryComplete,
        stripeCardCvcComplete: this.stripeCardCvcComplete,
        cardNumberElement: Boolean(this.cardNumberElement),
      });
      this.messageService.add({
        severity: 'error',
        summary: 'Payment Error',
        detail: 'Please fill in all card details correctly.',
      });
      return;
    }

    if (!this.order?.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Payment Error',
        detail: 'Order ID is missing. Please try again.',
      });
      return;
    }

    // Ensure Stripe is initialized before processing
    if (!this.stripe) {
      this.logger.debug('Ã°Å¸â€â€ž Initializing Stripe...');
      await this.initializeStripeIfNeeded();
    }

    // Ensure payment intent is fetched if not already available
    if (!this.paymentIntentClientSecret && this.isPaymentPending()) {
      this.logger.debug('Ã°Å¸â€â€ž Fetching payment intent...');
      this.fetchPaymentIntent();
      // Wait a bit for the fetch to complete
      await new Promise<void>(resolve => {
        setTimeout(resolve, 500);
      });
    }

    this.isProcessingPayment = true;

    try {
      // Ensure Stripe elements are mounted
      if (!this.cardNumberElement) {
        this.logger.debug('Ã°Å¸â€â€ž Initializing and mounting Stripe elements...');
        if (!this.stripe) {
          await this.initializeStripeIfNeeded();
        }
        this.mountStripeElements();
        // Wait for elements to mount
        await new Promise<void>(resolve => {
          setTimeout(resolve, 300);
        });
      }

      const cardNumberContainer = document.getElementById('card-number');
      if (cardNumberContainer && !cardNumberContainer.querySelector('.StripeElement')) {
        this.logger.debug('Ã°Å¸â€â€ž Re-mounting Stripe elements...');
        this.mountStripeElements();
        await new Promise<void>(resolve => {
          setTimeout(resolve, 300);
        });
      }

      // Get payment intent from order
      const intent = this.paymentIntentClientSecret || '';

      this.logger.debug(
        'Ã°Å¸â€Â Payment intent status:',
        intent ? 'Found' : 'Missing',
        intent ? `${intent.substring(0, 20)}...` : ''
      );

      if (!intent) {
        this.isProcessingPayment = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Payment Error',
          detail:
            'Payment intent client secret is missing. The order may need to be refreshed. Please try again.',
        });
        // Try to fetch payment intent one more time
        this.fetchPaymentIntent();
        return;
      }

      // Validate that intent contains '_secret_' to ensure it's a client secret
      if (!intent.includes('_secret_')) {
        this.isProcessingPayment = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Payment Error',
          detail: 'Invalid payment intent client secret format. Please try again.',
        });
        this.logger.error(
          'Invalid client secret format. Expected format: pi_xxx_secret_yyy, got:',
          intent
        );
        return;
      }

      // Ensure Stripe is available
      if (!this.stripe) {
        this.isProcessingPayment = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Payment Error',
          detail: 'Payment system not initialized. Please try again.',
        });
        return;
      }

      this.logger.debug('Ã¢Å“â€¦ Processing payment with Stripe...');

      const result = await this.stripe.confirmCardPayment(intent, {
        payment_method: {
          card: this.cardNumberElement,
          billing_details: {
            name: this.paymentForm.cardholderName || '',
            address: {
              line1: this.paymentForm.billingAddress || '',
            },
          },
        },
      });

      this.logger.debug('Ã¢Å“â€¦ Stripe payment result:', result);

      this.isProcessingPayment = false;

      if (result.error) {
        this.messageService.add({
          severity: 'error',
          summary: 'Payment Error',
          detail: result.error.message,
        });
        return;
      }

      // Call backend API after success
      const paymentIntent = result.paymentIntent;

      const request = {
        order_id: this.order.id,
        payment_method_id: paymentIntent?.payment_method || null,
      };

      this.apiService
        .postWithSuperAdminToken('payment/card-payment', request)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: response => {
            this.logger.debug('Card payment confirmed successfully:', response);

            // Fetch full order details to get vehicle and package information
            this.fetchOrderDetailsForSuccessModal();

            // Refresh transaction history and emit update event
            setTimeout(() => {
              this.fetchTransactionHistory();
              this.orderUpdated.emit();
            }, 1000);
          },
          error: error => {
            this.logger.error('Error confirming card payment:', error);
            // Payment was successful on Stripe side, but backend confirmation failed
            // Still refresh to show the payment
            setTimeout(() => {
              this.fetchTransactionHistory();
              this.orderUpdated.emit();
            }, 1000);
          },
        });
    } catch (err: any) {
      this.isProcessingPayment = false;
      this.logger.error('Error processing payment:', err);
      this.messageService.add({
        severity: 'error',
        summary: 'Payment Error',
        detail:
          err?.error?.message || err?.message || 'Payment processing failed. Please try again.',
      });
    }
  }

  /**
   * Fetch payment intent from order
   */
  private fetchPaymentIntent(): void {
    if (!this.order?.id) return;

    // Use ApiService directly to get raw order response with all fields
    this.apiService
      .getWithSuperAdminToken(`order/${this.order.id}`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (orderResponse: any) => {
          const { data } = orderResponse ?? {};
          const order = data ?? orderResponse;
          this.paymentIntentClientSecret =
            order?.payment_intent_client_secret ??
            order?.client_secret ??
            data?.payment_intent_client_secret ??
            data?.client_secret ??
            '';

          if (this.paymentIntentClientSecret) {
            this.logger.debug(
              'Ã¢Å“â€¦ Payment intent fetched:',
              `${this.paymentIntentClientSecret.substring(0, 20)}...`
            );
          } else {
            this.logger.warn(
              'Ã¢Å¡Â Ã¯Â¸Â Payment intent client secret not found in order response'
            );
            this.logger.warn('Ã¢Å¡Â Ã¯Â¸Â Available fields:', Object.keys(order || {}));
          }
        },
        error: error => {
          this.logger.error('Error fetching payment intent:', error);
        },
      });
  }

  /**
   * Initialize Stripe if needed
   */
  private async initializeStripeIfNeeded(): Promise<void> {
    if (this.stripe && this.cardNumberElement) {
      return;
    }
    try {
      this.stripe = await loadStripe(environment.stripePublicKey);
      if (!this.stripe) {
        throw new Error('Failed to load Stripe');
      }

      this.elements = this.stripe.elements({
        appearance: STRIPE_ELEMENTS_APPEARANCE,
      });

      this.cardNumberElement = this.elements.create(
        'cardNumber',
        createStripeElementConfig('8539 5932 9402 4739')
      );

      this.cardExpiryElement = this.elements.create(
        'cardExpiry',
        createStripeElementConfig('05/30')
      );

      this.cardCvcElement = this.elements.create('cardCvc', createStripeElementConfig('123'));

      this.logger.debug('Ã¢Å“â€¦ Stripe initialized successfully');
    } catch (error) {
      this.logger.error('Error initializing Stripe:', error);
    }
  }

  /**
   * Mount Stripe Elements
   */
  mountStripeElements(): void {
    if (!this.cardNumberElement || !this.cardExpiryElement || !this.cardCvcElement) {
      this.logger.warn('Stripe elements not initialized yet');
      return;
    }

    try {
      // Check if elements are already mounted
      const cardNumberContainer = document.getElementById('card-number');
      const cardExpiryContainer = document.getElementById('card-expiry');
      const cardCvcContainer = document.getElementById('card-cvc');

      if (!cardNumberContainer || !cardExpiryContainer || !cardCvcContainer) {
        this.logger.warn('Stripe element containers not found in DOM');
        return;
      }

      // Unmount if already mounted
      if (cardNumberContainer.querySelector('.StripeElement')) {
        try {
          this.cardNumberElement.unmount();
        } catch (e) {
          // Already unmounted
        }
      }
      if (cardExpiryContainer.querySelector('.StripeElement')) {
        try {
          this.cardExpiryElement.unmount();
        } catch (e) {
          // Already unmounted
        }
      }
      if (cardCvcContainer.querySelector('.StripeElement')) {
        try {
          this.cardCvcElement.unmount();
        } catch (e) {
          // Already unmounted
        }
      }

      // Mount elements
      this.cardNumberElement.mount('#card-number');
      this.setupStripeElementListeners(this.cardNumberElement, 'card-number-error');

      this.cardExpiryElement.mount('#card-expiry');
      this.setupStripeElementListeners(this.cardExpiryElement, 'card-expiry-error');

      this.cardCvcElement.mount('#card-cvc');
      this.setupStripeElementListeners(this.cardCvcElement, 'card-cvc-error');

      this.logger.debug('Ã¢Å“â€¦ Stripe Elements mounted successfully');
    } catch (error) {
      this.logger.error('Error mounting Stripe elements:', error);
    }
  }

  /**
   * Setup Stripe element listeners
   */
  private setupStripeElementListeners(element: any, errorElementId: string): void {
    const errorElement = document.getElementById(errorElementId);

    if (!errorElement) {
      return;
    }

    element.on('change', (event: any) => {
      if (event.error) {
        errorElement.textContent = event.error.message;
        errorElement.style.display = 'block';
        this.updateStripeElementCompletionState(errorElementId, false);
      } else {
        errorElement.textContent = '';
        errorElement.style.display = 'none';
        const isComplete = event.complete || false;
        this.updateStripeElementCompletionState(errorElementId, isComplete);
      }
    });

    element.on('focus', () => {
      errorElement.textContent = '';
      errorElement.style.display = 'none';
    });
  }

  /**
   * Update Stripe element completion state
   */
  private updateStripeElementCompletionState(errorElementId: string, isComplete: boolean): void {
    if (errorElementId === 'card-number-error') {
      this.stripeCardNumberComplete = isComplete;
    } else if (errorElementId === 'card-expiry-error') {
      this.stripeCardExpiryComplete = isComplete;
    } else if (errorElementId === 'card-cvc-error') {
      this.stripeCardCvcComplete = isComplete;
    }
  }

  /**
   * Format a date string into "D Month, YYYY".
   * @example
   * ormatDate('Oct 17 2025, 3.49 PM')
   * '17 Oct, 2025'
   * @param {{string}} {{dateString}} - Input date string which may already be formatted (e.g. "Oct 17 2025, 3.49 PM") or a raw date string parseable by Date.
   * @returns {{string}} Formatted date in "D Month, YYYY" form (e.g. "17 Oct, 2025" or "17 October, 2025"); returns 'Invalid Date' if the input cannot be parsed.
   **/
  formatDate(dateString: string): string {
    // Handle already formatted date strings from orders service
    if ((dateString.includes(',') && dateString.includes('PM')) || dateString.includes('AM')) {
      // This is already formatted as "Oct 17 2025, 3.49 PM" - extract just the date part
      const datePart = dateString.split(',')[0]; // "Oct 17 2025"
      const parts = datePart.split(' ');
      if (parts.length === 3) {
        const month = parts[0]; // "Oct"
        const day = parts[1]; // "17"
        const year = parts[2]; // "2025"
        return `${day} ${month}, ${year}`;
      }
    }

    // Handle raw date strings
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }

    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'long' });
    const year = date.getFullYear();
    return `${day} ${month}, ${year}`;
  }

  getTransactionType(): string {
    return this.order ? OrderUtils.getTransactionType(this.order) : 'Single';
  }

  getVehicleDisplayNameForOrder(): string {
    if (!this.order) return '2022 Porsche 911 GT3 RS';

    // Build vehicle name from available data
    const parts = [];
    if (this.order.vehicleInfo) {
      parts.push(this.order.vehicleInfo);
    }

    return parts.length > 0 ? parts.join(' ') : '2022 Porsche 911 GT3 RS';
  }

  isMembershipTransaction(): boolean {
    return this.getTransactionType() === 'Membership';
  }

  formatPrice(): string {
    return this.order ? OrderUtils.formatPrice(this.order) : '';
  }

  getVehicleName(): string {
    return this.order ? OrderUtils.getVehicleName(this.order) : 'Unknown Vehicle';
  }

  getVinNumber(): string {
    // Use the sample VIN from design for now, fallback to utility method
    return this.order?.vinNumber || '';
  }

  getFamilyPlanStatus(): string {
    // Use the sample value from design for now, fallback to utility method
    return this.order
      ? this.order.isFamilyPlanVehicle !== undefined
        ? this.order.isFamilyPlanVehicle
          ? 'Yes'
          : 'No'
        : 'Yes'
      : 'Yes';
  }

  get isSaveDisabled(): boolean {
    const hasRequired = Boolean(this.vehicleForm.state) && Boolean(this.vehicleForm.licensePlate);
    return !this.vehicleFetchSuccess || !hasRequired;
  }

  // Family Plan helpers
  /**
   * Try to load family-plan vehicles for the current order's customer. Avoids refetching if vehicles are already loaded, requests vehicles from the super-admin company/customer API, populates this.familyPlanVehicles, and sets this.selectedVehicleId by matching the order's license plate; on error clears familyPlanVehicles and silently falls back to the regular vehicle UI.
   * @example
   * tryLoadFamilyPlanVehicles()
   * undefined
   * @returns {{void}} Does not return a value; updates component state (familyPlanVehicles and selectedVehicleId).
   **/
  private tryLoadFamilyPlanVehicles(): void {
    this.logger.debug('Ã°Å¸Å¡â€” tryLoadFamilyPlanVehicles called', {
      orderId: this.order?.orderId,
      customerId: this.order?.customerId,
    });

    if (!this.order?.customerId) {
      this.logger.debug('Ã°Å¸Å¡â€” No customer ID available, exiting');
      return;
    }
    // Avoid refetching if already loaded
    if (this.familyPlanVehicles.length > 0) {
      this.logger.debug('Ã°Å¸Å¡â€” Family plan vehicles already loaded, skipping fetch');
      return;
    }

    this.logger.debug(
      'Ã°Å¸Å¡â€” Fetching family plan vehicles for customer:',
      this.order.customerId
    );
    if (!this.companyId || !this.order.customerId) {
      this.logger.debug('Ã°Å¸Å¡â€” Missing companyId or customerId, exiting');
      return;
    }
    this.apiService
      .getWithSuperAdminToken(
        `admin/super-admin/companies/${this.companyId}/customers/${this.order.customerId}/vehicles`
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.logger.debug('Ã°Å¸Å¡â€” Family plan vehicles API response:', res);
          const { data: rawVehicles } = res ?? {};
          const vehicles = rawVehicles ?? [];
          this.familyPlanVehicles = Array.isArray(vehicles) ? vehicles : [];
          this.logger.debug('Ã°Å¸Å¡â€” Processed family plan vehicles:', this.familyPlanVehicles);

          // Match the order's license plate with family plan vehicles
          this.logger.debug('Ã°Å¸Å¡â€” Calling findMatchingVehicleByLicensePlate...');
          this.selectedVehicleId = this.findMatchingVehicleByLicensePlate();
          this.logger.debug('Ã°Å¸Å¡â€” Selected vehicle ID:', this.selectedVehicleId);
        },
        error: error => {
          this.logger.debug('Ã°Å¸Å¡â€” Error fetching family plan vehicles:', error);
          // Silently ignore; will fall back to regular vehicle UI
          this.familyPlanVehicles = [];
        },
      });
  }

  isFamilyPlanOrder(): boolean {
    // Consider it family plan if API returned vehicles flagged as family plan
    return this.familyPlanVehicles.some(v => v.is_family_plan);
  }

  getVehicleDisplayName(vehicle: FamilyPlanVehicle): string {
    const year = vehicle.year ? String(vehicle.year) : '';
    const make = vehicle.make || '';
    const model = vehicle.model || '';
    return [year, make, model].filter(Boolean).join(' ').trim();
  }

  /**
   * Finds a vehicle in the current family plan whose license_plate matches the order's licensePlate (case-insensitive) and returns that vehicle's id, or null if no match or required data is missing.
   * @example
   * findMatchingVehicleByLicensePlate()
   * 'vehicle-id-123' // or null
   * @returns {string|null} Matched vehicle id when a license plate match is found; otherwise null.
   **/
  private findMatchingVehicleByLicensePlate(): string | null {
    if (!this.order || this.familyPlanVehicles.length === 0) {
      this.logger.debug('Ã°Å¸Å¡â€” No order or family plan vehicles available');
      return null;
    }

    this.logger.debug('Ã°Å¸Å¡â€” Order license plate:', this.order.licensePlate);
    this.logger.debug(
      'Ã°Å¸Å¡â€” Family plan vehicles:',
      this.familyPlanVehicles.map(v => ({
        id: v.id,
        license_plate: v.license_plate,
        make: v.make,
        model: v.model,
        year: v.year,
      }))
    );

    // Match by license plate from the order
    if (this.order?.licensePlate) {
      const matchByLicense = this.familyPlanVehicles.find(
        vehicle =>
          vehicle.license_plate &&
          vehicle.license_plate.toLowerCase() === this.order?.licensePlate?.toLowerCase()
      );

      if (matchByLicense) {
        this.logger.debug('Ã°Å¸Å¡â€” Ã¢Å“â€¦ Found matching vehicle by license plate:', {
          orderId: this.order?.orderId,
          orderLicensePlate: this.order?.licensePlate,
          matchedVehicle: {
            id: matchByLicense.id,
            license_plate: matchByLicense.license_plate,
            make: matchByLicense.make,
            model: matchByLicense.model,
          },
        });
        return matchByLicense.id;
      } else {
        this.logger.debug(
          'Ã°Å¸Å¡â€” Ã¢ÂÅ’ No vehicle found matching license plate:',
          this.order?.licensePlate
        );
      }
    } else {
      this.logger.debug('Ã°Å¸Å¡â€” Ã¢Å¡Â Ã¯Â¸Â Order has no license plate to match');
    }

    // No match found - leave unselected for user to choose
    return null;
  }

  selectVehicle(vehicleId: string): void {
    this.selectedVehicleId = vehicleId;
  }

  /**
   * Enable editing mode and populate the component form with data from a family plan vehicle.
   * @example
   * nEditFamilyVehicle(sampleVehicle, sampleEvent)
   * undefined
   * @param {{FamilyPlanVehicle}} {{vehicle}} - Family plan vehicle object whose data will be loaded into the edit form.
   * @param {{Event}} {{event}} - Optional DOM event; propagation is stopped if provided.
   * @returns {{void}} No return value; performs side effects to enter edit mode and populate the form.
   **/
  onEditFamilyVehicle(vehicle: FamilyPlanVehicle, event?: Event): void {
    event?.stopPropagation();

    // Enter edit mode for family plan vehicle
    this.editingFamilyVehicle = vehicle;
    this.isVehicleEditMode = true;
    this.vehicleFetchSuccess = false;

    // Populate form with family plan vehicle data
    this.vehicleForm = {
      state: '', // Will need to be filled or fetched
      licensePlate: vehicle.license_plate || '',
      vehicle: this.getVehicleDisplayName(vehicle),
      year: vehicle.year ? String(vehicle.year) : '',
      make: vehicle.make || '',
      model: vehicle.model || '',
    };

    this.logger.debug('Ã°Å¸Å¡â€” Editing family plan vehicle:', vehicle);
  }

  onDeleteFamilyVehicle(vehicle: FamilyPlanVehicle, event?: Event): void {
    event?.stopPropagation();
    this.messageService.add({
      severity: 'info',
      summary: 'Delete Vehicle',
      detail: `${this.getVehicleDisplayName(vehicle)} (coming soon)`,
    });
  }

  onAddVehicle(): void {
    this.messageService.add({
      severity: 'info',
      summary: 'Add Vehicle',
      detail: 'Add vehicle flow coming soon',
    });
  }

  onSelectVehicle(): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Vehicle Selected',
      detail: 'Vehicle selected successfully',
    });
  }

  onCancelFamilyPlan(): void {
    this.onClose();
  }

  onBackFamilyPlan(): void {
    this.setActiveTab('order-summary');
  }

  /**
   * Enable vehicle edit mode and populate the vehicle form from the current order; detects state from the license plate and attempts to fetch full vehicle details (year/make/model) from the API.
   * @example
   * nEditVehicle()
   * undefined
   * @returns {void} No return value.
   */
  onEditVehicle(): void {
    // Enter edit mode and seed form values from current order
    this.isVehicleEditMode = true;

    // Initialize form with empty values (no sample data)
    // Try to detect state from license plate format, fallback to CA
    const detectedState = this.detectStateFromLicensePlate(this.order?.licensePlate || '');

    this.vehicleForm = {
      state: detectedState,
      licensePlate: this.order?.licensePlate || '',
      vehicle: '',
      year: '',
      make: '',
      model: '',
    };

    // Set loading state
    this.isLoadingVehicleDetails = true;
    this.vehicleFetchSuccess = false;
    this.hasAttemptedFetch = false;

    // Fetch full order details from API to get accurate year/make/model
    if (this.order?.id) {
      this.apiService
        .getWithSuperAdminToken(`order/${this.order.id}`)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (orderResponse: any) => {
            this.isLoadingVehicleDetails = false;

            // Handle different response structures: { data: {...} } or direct order object
            const apiOrder = orderResponse?.data || orderResponse;

            // Extract vehicle information directly from API response
            const vehicleYear =
              apiOrder?.year?.toString() || apiOrder?.vehicle_year?.toString() || '';
            const vehicleMake = apiOrder?.make || apiOrder?.vehicle_make || '';
            const vehicleModel = apiOrder?.model || apiOrder?.vehicle_model || '';

            // Build vehicle name from API data
            const vehicleNameParts = [vehicleYear, vehicleMake, vehicleModel].filter(Boolean);
            const vehicleName = vehicleNameParts.length > 0 ? vehicleNameParts.join(' ') : '';

            // Check if vehicle is already fetched (has vehicle details)
            const hasVehicleDetails = Boolean(vehicleYear && vehicleMake && vehicleModel);
            this.vehicleFetchSuccess = hasVehicleDetails;
            this.hasAttemptedFetch = hasVehicleDetails; // If vehicle details exist, consider fetch already attempted

            // Update form with API data
            this.vehicleForm = {
              ...this.vehicleForm,
              vehicle: vehicleName,
              year: vehicleYear,
              make: vehicleMake,
              model: vehicleModel,
            };
          },
          error: error => {
            this.isLoadingVehicleDetails = false;
            this.logger.error('Error fetching order details for edit:', error);
            // Keep form empty on error - user can manually enter or fetch via license plate
            this.vehicleFetchSuccess = false;
            this.hasAttemptedFetch = false;
          },
        });
    } else {
      // No order ID available - keep form empty
      this.isLoadingVehicleDetails = false;
      this.vehicleFetchSuccess = false;
      this.hasAttemptedFetch = false;
    }
  }

  /**
   * Try to detect state from license plate format
   */
  private detectStateFromLicensePlate(licensePlate: string): string {
    if (!licensePlate) return 'CA'; // Default fallback

    // Common license plate patterns by state
    const statePatterns: Record<string, RegExp> = {
      CA: /^[0-9]{1}[A-Z]{3}[0-9]{3}$/, // California: 1ABC123
      NY: /^[A-Z]{3}[0-9]{4}$/, // New York: ABC1234
      TX: /^[A-Z]{3}[0-9]{4}$/, // Texas: ABC1234
      FL: /^[A-Z]{3}[0-9]{3}$/, // Florida: ABC123
      IL: /^[A-Z]{2}[0-9]{5}$/, // Illinois: AB12345
    };

    // Check patterns and return matching state
    for (const [state, pattern] of Object.entries(statePatterns)) {
      if (pattern.test(licensePlate.toUpperCase())) {
        return state;
      }
    }

    // If no pattern matches, return default
    return 'CA';
  }

  /**
   * Exit vehicle edit mode and reset related flags and the vehicle form to default empty values.
   * @example
   * nCancelVehicleEdit()
   * undefined
   * @returns {{void}} No return value; performs state reset only.
   **/
  onCancelVehicleEdit(): void {
    this.isVehicleEditMode = false;
    this.vehicleFetchSuccess = false;
    this.hasAttemptedFetch = false;
    this.editingFamilyVehicle = null;

    // Reset vehicle form
    this.vehicleForm = {
      state: '',
      licensePlate: '',
      vehicle: '',
      year: '',
      make: '',
      model: '',
    };
  }

  /**
   * Save the current vehicle edit on the active order (handles family-plan or regular vehicles) and exit vehicle edit mode.
   * @example
   * nSaveVehicleEdit()
   * // returns void
   * @returns {void} No return value; persists the edited vehicle (family or regular) and disables vehicle edit mode.
   */
  onSaveVehicleEdit(): void {
    if (!this.order || this.isSaveDisabled) {
      return;
    }

    if (this.editingFamilyVehicle) {
      // Save family plan vehicle edit
      this.saveFamilyPlanVehicleEdit();
    } else {
      // Save regular vehicle edit
      this.saveRegularVehicleEdit();
    }

    // Exit edit mode
    this.isVehicleEditMode = false;
    this.editingFamilyVehicle = null;
  }

  /**
   * Save edits from the vehicle form into the current order, update the UI immediately, and trigger a backend update when a vehicle ID is available; otherwise show a success message.
   * @example
   * saveRegularVehicleEdit()
   * void
   * @returns {{void}} Does not return a value.
   **/
  private saveRegularVehicleEdit(): void {
    const { year, make, model, licensePlate, fetchedVehicleId } = this.vehicleForm;
    const nameParts = [year, make, model].filter(Boolean).join(' ').trim();

    // Update UI immediately
    this.order!.vehicleInfo = nameParts || this.order!.vehicleInfo;
    this.order!.licensePlate = licensePlate || this.order!.licensePlate;

    // Use fetched vehicle ID if available, otherwise use existing vehicle ID
    const vehicleIdToUse = fetchedVehicleId || this.order!.vehicleId;

    if (vehicleIdToUse) {
      this.updateOrderVehicle(vehicleIdToUse);
    } else {
      // If no vehicle_id, just show success message
      this.messageService.add({
        severity: 'success',
        summary: 'Vehicle Updated',
        detail: 'Vehicle details have been updated successfully',
      });
    }
  }

  /**
   * Update the vehicle assigned to the current order by sending a PUT request as a super-admin.
   * @example
   * updateOrderVehicle('vehicle-123')
   * void
   * @param {{string}} {{vehicleId}} - ID of the vehicle to assign to the order.
   * @returns {{void}} Performs an asynchronous update and does not return a value.
   **/
  private updateOrderVehicle(vehicleId: string): void {
    const payload = {
      vehicle_id: vehicleId,
    };

    this.apiService
      .putWithSuperAdminToken(`order/${this.order!.id}`, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.logger.debug('Order updated successfully:', response);
        },
        error: error => {
          this.logger.error('Error updating order:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Update Failed',
            detail: 'Failed to update vehicle details. Please try again.',
          });
        },
      });
  }

  /**
   * Save the current edits from vehicleForm into the selected family plan vehicle and display a success message.
   * @example
   * saveFamilyPlanVehicleEdit()
   * void
   * @returns {void} No return value; updates the familyPlanVehicles array and displays a notification.
   **/
  private saveFamilyPlanVehicleEdit(): void {
    if (!this.editingFamilyVehicle) return;

    const { year, make, model, licensePlate } = this.vehicleForm;

    // Update the family plan vehicle in the local array
    const vehicleIndex = this.familyPlanVehicles.findIndex(
      v => v.id === this.editingFamilyVehicle!.id
    );
    if (vehicleIndex !== -1) {
      this.familyPlanVehicles[vehicleIndex] = {
        ...this.familyPlanVehicles[vehicleIndex],
        year: year ? parseInt(year, 10) : this.familyPlanVehicles[vehicleIndex].year,
        make: make || this.familyPlanVehicles[vehicleIndex].make,
        model: model || this.familyPlanVehicles[vehicleIndex].model,
        license_plate: licensePlate || this.familyPlanVehicles[vehicleIndex].license_plate,
      };

      this.logger.debug(
        'Ã°Å¸Å¡â€” Family plan vehicle updated:',
        this.familyPlanVehicles[vehicleIndex]
      );
      this.messageService.add({
        severity: 'success',
        summary: 'Vehicle Updated',
        detail: `${this.getVehicleDisplayName(this.familyPlanVehicles[vehicleIndex])} has been updated`,
      });
    }
  }

  /**
   * Fetch vehicle details for the current vehicleForm using a super-admin token, update the form with returned data, and manage loading and error/success states.
   * @example
   * nFetchVehicleDetails()
   * undefined
   * @param {{void}} {{none}} - No arguments.
   * @returns {{void}} Void.
   **/
  onFetchVehicleDetails(): void {
    if (!this.vehicleForm.state || !this.vehicleForm.licensePlate) {
      return;
    }

    this.isLoadingVehicle = true;
    this.hasAttemptedFetch = true; // Mark that fetch has been attempted - fields will remain readonly

    // Fetch vehicle data using super admin token
    const state = this.vehicleForm.state;
    const licensePlate = this.vehicleForm.licensePlate.trim();

    this.apiService
      .getWithSuperAdminToken(`vehicle/get-vehicle-data/${state}/${licensePlate}`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.logger.debug('Vehicle data fetched successfully:', response);
          this.isLoadingVehicle = false;
          const { data: vehicleData, status } = response ?? {};
          if (status && vehicleData) {
            // Update form with fetched vehicle data for user review
            this.vehicleForm = {
              ...this.vehicleForm,
              vehicle:
                vehicleData.make && vehicleData.model
                  ? `${vehicleData.year} ${vehicleData.make} ${vehicleData.model}`
                  : this.vehicleForm.vehicle,
              year: vehicleData.year ? String(vehicleData.year) : this.vehicleForm.year,
              make: vehicleData.make || this.vehicleForm.make,
              model: vehicleData.model || this.vehicleForm.model,
              fetchedVehicleId: vehicleData.id, // Store the fetched vehicle ID
            };

            this.vehicleFetchSuccess = true;
          } else {
            this.clearVehicleFetchState(response.message || 'Failed to fetch vehicle data');
          }
        },
        error: error => {
          this.logger.error('Error fetching vehicle data:', error);
          this.isLoadingVehicle = false;
          this.clearVehicleFetchState(
            error.error?.message || 'Failed to fetch vehicle data. Please try again.'
          );
        },
      });
  }

  /** Clear vehicle form fields and show a "Fetch Failed" error toast. Shared by the else and error paths of onFetchVehicleDetails. */
  private clearVehicleFetchState(errorDetail: string): void {
    this.vehicleForm = {
      ...this.vehicleForm,
      vehicle: '',
      year: '',
      make: '',
      model: '',
      fetchedVehicleId: undefined,
    };
    this.vehicleFetchSuccess = false;
    this.messageService.add({
      severity: 'error',
      summary: 'Fetch Failed',
      detail: errorDetail,
    });
  }

  /**
   * Parse a vehicle name string into year, make, and model.
   * @example
   * parseVehicleName('2022 Porsche 911 GT3 RS')
   * { year: '2022', make: 'Porsche', model: '911 GT3 RS' }
   * @param {{string}} {{name}} - The vehicle name string to parse.
   * @returns {{ year: string; make: string; model: string }} Parsed parts of the vehicle name (year, make, model).
   **/
  private parseVehicleName(name: string): { year: string; make: string; model: string } {
    const tokens = name.trim().split(/\s+/);
    const [first = '', second = '', ...rest] = tokens;
    let year = '';
    let make = '';
    let model = '';

    if (tokens.length > 0 && /^\d{4}$/.test(first)) {
      year = first;
      make = second;
      model = rest.join(' ');
    } else {
      make = first;
      model = [second, ...rest].filter(Boolean).join(' ');
    }

    return { year, make, model };
  }

  /**
   * Opens a confirmation dialog to delete the current vehicle from the order.
   * @example
   * nDeleteVehicle()
   * void
   * @returns {void} Does not return a value.
   */
  onDeleteVehicle(): void {
    if (!this.order) return;
    // TODO: implement vehicle deletion with Material dialog
    // Currently stubbed - this.confirmationService.confirm({...}) is no longer available
  }

  get vehicleCardData(): Vehicle | null {
    if (!this.order) return null;
    return {
      id: this.order.id,
      name: this.getVehicleName(),
      licensePlate: this.order.licensePlate,
      vin: this.getVinNumber(),
      isFamilyPlanVehicle: this.order.isFamilyPlanVehicle ?? true,
    };
  }

  onVehicleEdit(): void {
    this.onEditVehicle();
  }

  onVehicleDelete(): void {
    this.onDeleteVehicle();
  }

  // Mobile menu methods for vehicle actions
  toggleVehicleMobileMenu(event: Event): void {
    event.stopPropagation();
    this.showVehicleMobileMenu = !this.showVehicleMobileMenu;
  }

  // Dropdown functionality
  toggleDropdown(event: Event): void {
    event.stopPropagation();
    this.showDropdown = !this.showDropdown;
  }

  // Close dropdown when clicking outside
  @HostListener('document:click')
  onDocumentClick(): void {
    this.showDropdown = false;
    this.showVehicleMobileMenu = false;
  }

  // Cancel order functionality
  canCancelOrder(): boolean {
    if (!this.order) {
      return false;
    }

    return OrderUtils.canBeCancelled(this.order);
  }

  canShowActions(): boolean {
    // Always show actions (three dots) - view option is always available
    return true;
  }

  /**
   * Close the cancel-order flow by hiding the dropdown and showing the cancel order modal for the current order, with diagnostic logging.
   * @example
   * nCancelOrder()
   * undefined
   * @returns {{void}} No return value; triggers UI changes to show the cancel order modal.
   **/
  onCancelOrder(): void {
    this.logger.debug('Ã°Å¸Å¡Â« Cancel Order clicked in order details modal');
    this.logger.debug('Ã°Å¸â€œâ€¹ Order:', this.order);
    this.logger.debug('Ã¢Å“â€¦ Can cancel order:', this.canCancelOrder());

    if (!this.order) {
      this.logger.debug('Ã¢ÂÅ’ No order found, returning');
      return;
    }

    this.showDropdown = false;
    this.showCancelOrderModal = true;
    this.logger.debug('Ã°Å¸â€Â§ Show cancel order modal set to:', this.showCancelOrderModal);
  }

  /**
   * Handle confirmation to cancel an order: either trigger a "book again" flow or call the cancel API, update local order state, emit cancellation events, and display the appropriate modals.
   * @example
   * nCancelOrderConfirm({ order: { id: 'orderId' }, reason: 'Customer requested', action: 'book-again' })
   * void
   * @param {{ { order: Order; reason: string; action?: string } }} {{data}} - Data object containing the order to cancel, a cancellation reason, and an optional action flag.
   * @returns {{void}} Returns nothing.
   **/
  onCancelOrderConfirm(data: { order: Order; reason: string; action?: string }): void {
    // Check if action is 'book-again' to trigger create order
    if (data.action === 'book-again') {
      // Close the order details modal and trigger add order modal
      this.onClose();
      // You might need to emit an event to the parent to show add order modal
      // For now, we'll just close the modal
    } else {
      // Call the cancel order API using super admin token
      const requestBody = {
        cancelled_by: 'admin',
        cancellation_reason: data.reason,
      };

      this.apiService
        .putWithSuperAdminToken(`order/cancel/${data.order.id}`, requestBody)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response: any) => {
            if (response.status) {
              // Check if it was a scheduled order before updating status
              const wasScheduledOrder = this.order?.status?.value === 'scheduled';

              // Store the order price before updating status
              const orderPrice = this.order?.price || '';

              // Update the order status locally
              if (this.order) {
                this.order.status = { label: 'Canceled', value: 'canceled', color: 'red' };
              }

              // Emit the cancelled order to parent component
              if (this.order) {
                this.orderCancelled.emit(this.order);
              }

              // Set properties for cancellation success modal
              this.cancellationWasScheduled = wasScheduledOrder;
              this.cancellationRefundAmount = orderPrice;
              this.showCancellationSuccessModal = true;
            } else {
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: CANCEL_ORDER_ERROR_DETAIL,
              });
            }
          },
          error: error => {
            this.logger.error('Error cancelling order:', error);
            this.logger.error('Error details:', {
              status: error.status,
              statusText: error.statusText,
              message: error.message,
              error: error.error,
            });
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: CANCEL_ORDER_ERROR_DETAIL,
            });
          },
        });
    }

    this.closeCancelOrderModal();
  }

  onCancellationSuccessClose(): void {
    this.showCancellationSuccessModal = false;
  }

  onCancellationBookAgain(): void {
    // Close the cancellation success modal
    this.showCancellationSuccessModal = false;
    // Close the order details modal
    this.onClose();
    // Emit event to parent to open add order modal
    this.bookAgain.emit();
  }

  /**
   * Parse vehicle info string to extract make, model, and year
   * Format: "Make Model (LicensePlate)" or "Year Make Model (LicensePlate)"
   */
  private parseVehicleInfo(vehicleInfo: string): { year: string; make: string; model: string } {
    if (!vehicleInfo) {
      return { year: '', make: '', model: '' };
    }

    // Remove license plate part (everything in parentheses)
    const withoutPlate = vehicleInfo.replace(/\s*\([^)]*\)\s*$/, '').trim();

    // Split by spaces
    const parts = withoutPlate.split(/\s+/).filter(p => p);

    if (parts.length === 0) {
      return { year: '', make: '', model: '' };
    }

    const [p0 = '', p1 = '', ...rest] = parts;
    let year = '';
    let make = '';
    let model = '';

    if (parts.length >= 1 && /^\d{4}$/.test(p0)) {
      year = p0;
      if (parts.length >= 2) {
        make = p1;
        if (parts.length >= 3) {
          model = rest.join(' ');
        }
      }
    } else if (parts.length >= 1) {
      make = p0;
      if (parts.length >= 2) {
        model = [p1, ...rest].join(' ');
      }
    }

    return { year, make, model };
  }

  /**
   * Get package name from order, removing "Single Wash" or "Monthly Membership" suffix
   */
  private getPackageNameFromOrder(): string {
    if (this.order?.packageName) {
      let packageName = this.order.packageName;
      packageName = packageName.replace(/^Car\s+Wash\s+Package\s+/i, '').trim();
      return packageName || '';
    }
    if (!this.order?.washType) {
      return '';
    }
    let packageName = this.order.washType;
    // Remove "Car Wash Package" prefix if it exists
    packageName = packageName.replace(/^Car\s+Wash\s+Package\s+/i, '').trim();
    // Remove common suffixes
    packageName = packageName
      .replace(/\s*(Single\s+Wash|Monthly\s+Membership|Single|Monthly|Membership)\s*$/i, '')
      .trim();

    return packageName || '';
  }

  /**
   * Handle payment success modal close
   */
  onPaymentSuccessModalClose(): void {
    this.showPaymentSuccess = false;
  }

  /**
   * Handle payment success modal action
   */
  onPaymentSuccessAction(): void {
    this.showPaymentSuccess = false;
  }

  /**
   * Public method to show payment success modal
   * Called from parent components when payment succeeds via pusher (e.g., tap to pay)
   */
  showPaymentSuccessModal(): void {
    this.fetchOrderDetailsForSuccessModal();
  }

  /**
   * Fetch full order details from API to populate success modal
   */
  private fetchOrderDetailsForSuccessModal(): void {
    if (!this.order?.id) {
      // Fallback to basic data if no order ID
      this.populateSuccessModalWithBasicData();
      return;
    }

    // Fetch full order details from API
    this.apiService
      .getWithSuperAdminToken(`order/${this.order.id}`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (orderResponse: any) => {
          // Handle different response structures: { data: {...} } or direct order object
          const apiOrder = orderResponse?.data || orderResponse;

          let fields = extractPaymentSuccessFieldsFromApiOrder(apiOrder, {
            customerName: this.order?.customerName || 'Customer',
          });
          if (!fields.packageName) {
            fields = {
              ...fields,
              packageName: normalizePackageNameForDisplay(this.getPackageNameFromOrder()),
            };
          }

          this.populateSuccessModal(apiOrder, fields);
        },
        error: error => {
          this.logger.error('Error fetching order details:', error);
          // Fallback to basic data
          this.populateSuccessModalWithBasicData();
        },
      });
  }

  /**
   * Populate success modal with fetched data
   */
  private populateSuccessModal(
    apiOrder: Record<string, any>,
    fields: PaymentSuccessOrderFields
  ): void {
    this.successModalData = buildPaymentSuccessModalData(
      extractPaymentSuccessFieldsFromApiOrder(apiOrder, {
        ...fields,
        customerName: fields.customerName || this.order?.customerName || 'Customer',
      })
    );
    this.showPaymentSuccess = true;
  }

  /**
   * Populate success modal with basic data as fallback
   */
  private populateSuccessModalWithBasicData(): void {
    const vehicleInfo = this.parseVehicleInfo(this.order?.vehicleInfo || '');
    this.successModalData = buildPaymentSuccessModalData({
      packageName: this.getPackageNameFromOrder(),
      customerName: this.order?.customerName || 'Customer',
      vehicleYear: vehicleInfo.year || '',
      vehicleMake: vehicleInfo.make || '',
      vehicleModel: vehicleInfo.model || '',
    });
    this.showPaymentSuccess = true;
  }

  closeCancelOrderModal(): void {
    this.showCancelOrderModal = false;
  }

  // Payment Link Sent Modal methods
  onPaymentLinkSentModalClose(): void {
    this.showPaymentLinkSentModal = false;
  }

  onCancelPayment(): void {
    this.showPaymentLinkSentModal = false;
    // Handle cancel payment logic here
  }

  onResendPaymentLink(): void {
    // Call the same createPaymentLink method to resend the link
    this.createPaymentLink();
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
